import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ForgotpasswordModalComponent } from '../forgotpassword-modal/forgotpassword-modal.component';
import { Login } from 'src/app/models/login';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/services/loader.service';
import { LoginResponses } from 'src/app/models/login-responses';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';
import { SignupModalComponent } from '../signup-modal/signup-modal.component';
import { UserService } from 'src/app/services/user.service';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.scss'],
})
export class LoginModalComponent implements OnDestroy {
  item: Login = new Login();
  modalReference = null;
  isDisableLoginBtn = false;
  returnURL: string = '';
  // #region INPUT, OUTPUT, VIEWCHILD

  @Output() onLoginClick = new EventEmitter<any>();
  @Output() onCloseBtnClick = new EventEmitter<any>();

  @ViewChild('loginModal') loginTemplate!: TemplateRef<LoginModalComponent>;
  @ViewChild('forgotPasswordModal')
  forgotPasswordComponent!: ForgotpasswordModalComponent;
  @ViewChild('signUpModal') signUpComponent!: SignupModalComponent;
  // #endregion
  private destroy$ = new Subject<void>();
  private destroyed = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private loaderService: LoaderService,
    private userService: UserService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute
  ) {
    this.returnURL = this.authService.getReturnURL();
    this.authService.returnUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.returnURL = value;
      });
  }



  ngOnInit(): void {
    const currentPath = this.router.url;
    if (currentPath.includes('instagram-callback') || currentPath.includes('insta-callback')) {
      const code = this.activatedRoute.snapshot.queryParamMap.get('code');
      if (code) {
        this.sendTokenToBackend(code, "instagram");
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  formGroup = new FormGroup({
    Email: new FormControl('', [
      Validators.required,
      CustomValidators.emailValidator(),
    ]),
    Password: new FormControl('', [
      Validators.required,
    ]),
  });

  openModal(content: TemplateRef<any>, windowClass: string = '') {
    this.modalReference = this.modalService.open(content, {
      windowClass: `project-modal ${windowClass}`,
      centered: true,
      backdropClass: 'primary-backdrop',
    });
  }

  async openSignUpModal(content: TemplateRef<any>, windowClass: string = '', currentModal?: any) {
    const isAllowedCountry = await this.userService.getUserCountry();
    if (isAllowedCountry) {
      this.signUpComponent.prepareRegistrationFlow();
      if (currentModal) {
        currentModal.close('Close click');
      }
      this.openModal(content, windowClass);
    } else {
      this.toastr.info("We're sorry, but registration is currently not available in your region. As a result, you will not be able to create an account at this time. We appreciate your interest and apologize for any inconvenience.");
    }
  }

  onTextValueChange(field) {
    this.fromGroupTouched();
    this.item[field.text] = field.value;
  }

  fromGroupTouched(isSubmit: boolean = false) {
    if (this.formGroup.touched || isSubmit) {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }

  closeAllModals() {
    this.modalService.dismissAll();
  }

  onClick() {
    if (this.isDisableLoginBtn) return;
    this.fromGroupTouched(true);
    if (this.formGroup.invalid) return;
    this.item.email = this.formGroup.get('Email').value;
    this.item.password = this.formGroup.get('Password').value;
    void this.completeLogin(this.authService.login(this.item));
  }

  private async completeLogin(response: Observable<LoginResponses>): Promise<void> {
    this.loaderService.setLoading(true);
    this.isDisableLoginBtn = true;
    try {
      const res = await firstValueFrom(response.pipe(takeUntil(this.destroy$)));
      // AuthService persists the accepted session before emitting the response.
      // Never restore a token if the user signed out while this flow was settling.
      if (!res?.access_token || this.destroyed || this.authService.getToken() !== res.access_token) return;

      const user = await firstValueFrom(this.userService.getPersonalDetails().pipe(takeUntil(this.destroy$)));
      if (this.destroyed || this.authService.getToken() !== res.access_token) return;
      this.userService.updateUser(user);
      localStorage.setItem('ProfilePictureName', user.profilePicture || '');
      const image = await firstValueFrom(this.userService.getProfilePicture(user.profilePicture).pipe(takeUntil(this.destroy$)));
      if (this.destroyed || this.authService.getToken() !== res.access_token) return;
      this.userService.setProfilePictureUrl(image);
      this.fromReset();
      this.onLoginClick.emit(image);
      this.toastr.success('Login successful!');
      this.closeAllModals();

      if (this.returnURL) {
        this.router.navigate([this.returnURL]);
        this.authService.updateReturnURL('');
      } else {
        this.router.navigateByUrl('', { skipLocationChange: true }).then(() => this.router.navigate(['']));
      }
    } catch {
      // Required profile/auth failures are already reported by the HTTP layer.
      // Always settle the form; an async subscription previously left it stuck.
    } finally {
      this.item.password = '';
      this.formGroup.get('Password').reset();
      this.isDisableLoginBtn = false;
      this.loaderService.setLoading(false);
    }
  }

  fromReset() {
    this.formGroup.reset();
    this.formGroup.updateValueAndValidity();
    this.item = new Login();
    this.isDisableLoginBtn = false;
    this.onCloseBtnClick.emit();
  }

  onClickOtp() {
    this.modalReference.close();
  }

  handleCloseClick() {
    this.closeAllModals();
  }
  async loginWithGoogle() {
    const credential = await this.authService.loginWithGoogle();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  async loginWithFacebook() {
    const credential = await this.authService.loginWithFacebook();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  async loginWithApple() {
    const credential = await this.authService.loginWithApple();
    if (credential != null) {
      this.sendTokenToBackend(credential.providerToken, credential.loginType);
    }
  }

  sendTokenToBackend(providerToken: string, loginType: string) {
    if (this.isDisableLoginBtn) return;
    void this.completeLogin(this.authService.sendTokenToBackend(providerToken, loginType));
  }

  async loginWithInstagram() {
    this.authService.loginWithInstagram();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
